"use client";

import React, { useEffect, useRef, useState, useMemo } from "react";
import styles from "../about.module.css";
import YearBg from "./YearBg";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import { useResponsive } from "@/hooks/useResponsive";
import { Title, Description } from "@/components/Common/Typography";

export default function TimelineSection({
  apiData,
  title,
  subtitle,
}: {
  apiData: any;
  title: string;
  subtitle: string;
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [active, setActive] = useState(0);
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const [showNav, setShowNav] = useState(false);
  const { isMobile, isTablet } = useResponsive();

  const timelineData = useMemo(() => {
    return parseTimelineData(apiData);
  }, [apiData]);

  const sortedData = useMemo(() => {
    return [...timelineData].reverse().map((item, index, arr) => ({
      ...item,
      isLast: index === arr.length - 1,
    }));
  }, [timelineData]);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const isInView =
        rect.top < window.innerHeight * 0.4 &&
        rect.bottom > window.innerHeight * 0.4;
      setShowNav(isInView);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const index = Number(e.target.getAttribute("data-index"));
          if (e.isIntersecting) {
            setActive(index);
            setVisibleItems((prev) =>
              prev.includes(index) ? prev : [...prev, index]
            );
          }
        });
      },
      { threshold: 0.4 }
    );

    itemRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [sortedData]);

  const scrollTo = (i: number) => {
    itemRefs.current[i]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  return (
    <section className={styles.timelineWrap} ref={sectionRef}>
      {/* NAV */}
      <div className={`${styles.timelineNav} ${showNav ? styles.navShow : ""}`}>
        {sortedData.map((item, i) => (
          <button
            key={item.id}
            onClick={() => scrollTo(i)}
            className={`${styles.navBtnTimeline} ${i === active ? styles.navBtnTimelineActive : ""}`}
          >
            {item.year}
          </button>
        ))}
      </div>

      {/* Header */}
      <div className={styles.timelineHeader}>
        <div className={styles.titleLine}></div>
        <div className="text-center px-8 flex flex-col items-center">
          <Title
            size="large"
            as="h2"
            align="center"
            color="#0e7d49"
            // className={styles.timelineTitleMain}
            style={{ marginBottom: "8px" }}
          >
            {title}
          </Title>
          {subtitle && (
            <Description
              as="p"
              size="medium"
              align="center"
              color="#000000ff"
              // className={styles.timelineSubtitle}
            >
              {subtitle}
            </Description>
          )}
        </div>
        <div className={styles.titleLine}></div>
      </div>

      {/* ITEMS */}
      <div className={styles.timelineList}>
        {sortedData.map((item, i) => {
          const isReverse = i % 2 === 0 && !item.isLast;

          return (
            <div
              key={item.id}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              data-index={i}
              className={`${styles.timelineItem} ${
                visibleItems.includes(i) ? styles.visible : ""
              }`}
            >
              {/* HÀNG 1: TEXT & HÌNH CHÍNH (picture) */}
              <div
                className={`${styles.timelineMainRow} ${
                  isReverse ? styles.reverse : ""
                }`}
              >
                {/* TEXT */}
                <div
                  className={`${styles.timelineText} ${
                    item.isLast ? styles.timelineText_6 : ""
                  }`}
                >
                  <div className={styles.yearWrap}>
                    <YearBg />
                    <span>{item.year}</span>
                  </div>
                  <Title
                    size="medium"
                    as="h3"
                    color="#0d5735"
                    align="center"
                    style={{
                      maxWidth: "80%",
                    }}
                  >
                    {item.title}
                  </Title>
                  <Description as="p" size="small" align="center" color="#292929ff">
                    {item.content}
                  </Description>
                </div>

                {/* HÌNH CHÍNH */}
                {!item.isLast && item.image && (
                  <div className={styles.imageContainer}>
                    <img
                      className={styles.timelineImage}
                      src={item.image}
                      alt={item.title}
                      loading="lazy"
                    />
                  </div>
                )}
              </div>

              {/* HÀNG 2: 3 PIC (picture 2 ở giữa, picture 3 và 4 ở 2 viền) NẰM THÀNH 1 HÀNG */}
              {!item.isLast &&
                (item.decoSide || item.decoCenter || item.decoOpSide) && (
                  <div
                    className={`${styles.timelineConnectorRow} ${
                      isReverse ? styles.reverse : ""
                    }`}
                  >
                    {/* Viền trái: Picture 3 */}
                    <div className={styles.connectorLeft}>
                      {item.decoSide && (
                        <img
                          className={`${styles.connectorDecoImg} ${styles.decoPic3}`}
                          src={item.decoSide}
                          alt=""
                          loading="lazy"
                        />
                      )}
                    </div>

                    {/* Ở giữa: Picture 2 (Line kết nối) */}
                    <div className={styles.connectorCenter}>
                      {item.decoCenter && (
                        <img
                          className={styles.connectorLineImg}
                          src={item.decoCenter}
                          alt=""
                          loading="lazy"
                        />
                      )}
                    </div>

                    {/* Viền phải: Picture 4 */}
                    <div className={styles.connectorRight}>
                      {item.decoOpSide && (
                        <img
                          className={`${styles.connectorDecoImg} ${styles.decoPic4}`}
                          src={item.decoOpSide}
                          alt=""
                          loading="lazy"
                        />
                      )}
                    </div>
                  </div>
                )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function parseTimelineData(apiData: any[]) {
  if (!Array.isArray(apiData)) return [];

  return apiData
    .sort((a, b) => a.stt - b.stt)
    .map((item) => {
      const cleanContent =
        item?.content
          ?.replace(/<\/?p>/g, "")
          ?.replace(/<br\s*\/?>/g, "\n")
          ?.trim() || "";

      return {
        id: item.summary || item.__id_tmp__,
        year: item.content_3 || "",
        title: item.summary || "",
        content: cleanContent,
        image: appendImageUrlFromAPI({ src: item.picture, size: "2k" }),
        decoCenter: appendImageUrlFromAPI({ src: item.picture_2, size: "2k" }),
        decoSide: appendImageUrlFromAPI({ src: item.picture_3, size: "2k" }),
        decoOpSide: appendImageUrlFromAPI({ src: item.picture_4, size: "2k" }),
      };
    });
}
